import * as yup from 'yup';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, HelpCircle, Settings, Trash2 } from 'react-feather';
import { FormManager, useForm } from 'react-form-state-manager';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBag, getServerErrors } from '../../../../common/helpers';
import { Event } from './helpers';
import { JoinPolicy, MemberVisibility, TranslationInput } from '../../../../__generated__/graphql';
import { TranslatableInput } from '../../../Translations/Inputs';
import { translationsForAttribute } from '../../../Translations/helpers';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useIntegerInput from '../../../../common/useIntegerInput';
import useLocale from '../../../../common/useLocale';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useValidation from '../../../../common/useValidation';

type TicketPermissionKey = 'allow_individuals' | 'allow_create_team' | 'allow_join_team';
type TeamPermissionMode = 'none' | 'create_join' | 'create_only' | 'join_only';
type Ticket = Event['tickets'][number];

const teamPermissionModeOptions = [
  { value: 'create_join', label: 'team_permissions_create_join' },
  { value: 'create_only', label: 'team_permissions_only_create' },
  { value: 'join_only', label: 'team_permissions_only_join' },
];

interface TeamTypeTicketPermissions {
  ticket_id: string;
  allow_individuals: boolean;
  allow_create_team: boolean;
  allow_join_team: boolean;
}

interface TeamTypeFormData {
  form_key: string;
  id?: string | null;
  title: string | null;
  title_translations: TranslationInput[];
  join_policy: JoinPolicy;
  member_visibility: MemberVisibility;
  max_team_size: number | null;
  tickets: TeamTypeTicketPermissions[];
}

interface EditEventTeamSettingsFormValues {
  team_types: TeamTypeFormData[];
}

interface TicketCategoryGroup {
  id: string;
  title: string;
  tickets: Ticket[];
}

const hasTeamActions = (ticket: TeamTypeTicketPermissions) => (
  ticket.allow_create_team || ticket.allow_join_team
);

const isBusinessGroupTicket = (ticket: Ticket) => (
  ticket.business && ticket.units > 1
);

const getDefaultPermissionMode = (ticket: Ticket): TeamPermissionMode => (
  isBusinessGroupTicket(ticket) ? 'create_only' : 'create_join'
);

const getDefaultAllowIndividuals = (ticket: Ticket) => !isBusinessGroupTicket(ticket);

const requiresTeams = (ticket: TeamTypeTicketPermissions) => (
  hasTeamActions(ticket) && !ticket.allow_individuals
);

const getTeamPermissionMode = (ticket: TeamTypeTicketPermissions): TeamPermissionMode => {
  if (!hasTeamActions(ticket)) {
    return 'none';
  }

  if (ticket.allow_create_team && ticket.allow_join_team) {
    return 'create_join';
  }

  if (ticket.allow_create_team) {
    return 'create_only';
  }

  return 'join_only';
};

const getTeamActionFlagsFromMode = (
  mode: TeamPermissionMode,
) => {
  if (mode === 'create_join') {
    return { allowCreateTeam: true, allowJoinTeam: true };
  }

  if (mode === 'create_only') {
    return { allowCreateTeam: true, allowJoinTeam: false };
  }

  if (mode === 'join_only') {
    return { allowCreateTeam: false, allowJoinTeam: true };
  }

  return { allowCreateTeam: false, allowJoinTeam: false };
};

const editEventTeamSchema = yup.object({
  team_types: yup.array().of(yup.object({
    title: yup.string().nullable().test('required', '', (value, { path }) => (
      path === 'team_types[0].title' || !!value
    )),
  })),
});

const getValues = (event: Event): EditEventTeamSettingsFormValues => {
  const ticketLookup = Object.fromEntries(event.tickets.map((ticket) => [ticket.id, ticket]));

  return {
    team_types: event.team_types.map((teamType) => ({
      form_key: teamType.id,
      id: teamType.id,
      title: teamType.title,
      title_translations: translationsForAttribute(teamType.translations, 'title'),
      join_policy: teamType.join_policy,
      member_visibility: teamType.member_visibility,
      max_team_size: teamType.max_team_size,
      deletable: teamType.deletable,
      tickets: teamType.tickets.map(({ id }) => ({
        ticket_id: id,
        allow_individuals: ticketLookup[id]?.allow_individuals || false,
        allow_create_team: ticketLookup[id]?.allow_create_team || false,
        allow_join_team: ticketLookup[id]?.allow_join_team || false,
      })),
    })),
  };
};

const getPermissionTicketIds = (
  teamTypes: TeamTypeFormData[],
  permission: TicketPermissionKey,
) => Array.from(new Set(
  teamTypes
    .flatMap((teamType) => teamType.tickets)
    .filter((ticket) => ticket[permission])
    .map((ticket) => ticket.ticket_id),
));

const getSaveTeamTypesInput = (teamTypes: TeamTypeFormData[]) => teamTypes.map((teamType) => ({
  // Backend stores order using array position during save (position = index + 1).
  id: teamType.id,
  title: teamType.title,
  title_translations: teamType.title_translations,
  join_policy: teamType.join_policy,
  member_visibility: teamType.member_visibility,
  max_team_size: teamType.max_team_size,
  tickets: teamType.tickets.filter(hasTeamActions).map((ticket) => ticket.ticket_id),
}));

const getTicketPermissions = (teamType: TeamTypeFormData, ticketId: string): TeamTypeTicketPermissions => (
  teamType.tickets.find(({ ticket_id }) => ticket_id === ticketId) || {
    ticket_id: ticketId,
    allow_individuals: true,
    allow_create_team: false,
    allow_join_team: false,
  }
);

const setTicketPermission = (
  teamType: TeamTypeFormData,
  ticketId: string,
  permission: TicketPermissionKey,
  checked: boolean,
): TeamTypeFormData => {
  const nextTicket: TeamTypeTicketPermissions = {
    ...getTicketPermissions(teamType, ticketId),
    [permission]: checked,
  };

  // If teams are disabled, then individuals are allowed.
  if (!hasTeamActions(nextTicket)) {
    nextTicket.allow_individuals = true;
  }

  const hasExisting = teamType.tickets.some(({ ticket_id }) => ticket_id === ticketId);

  // Insert or replace in the array.
  const nextTickets = hasExisting
    ? teamType.tickets.map((ticket) => (ticket.ticket_id === ticketId ? nextTicket : ticket))
    : [...teamType.tickets, nextTicket];

  return {
    ...teamType,
    tickets: nextTickets.filter(hasTeamActions),
  };
};

const setTicketTeamActions = (
  teamType: TeamTypeFormData,
  ticketId: string,
  mode: TeamPermissionMode,
): TeamTypeFormData => {
  const { allowCreateTeam, allowJoinTeam } = getTeamActionFlagsFromMode(mode);
  const nextTicket: TeamTypeTicketPermissions = {
    ...getTicketPermissions(teamType, ticketId),
    allow_create_team: allowCreateTeam,
    allow_join_team: allowJoinTeam,
  };

  if (!hasTeamActions(nextTicket)) {
    nextTicket.allow_individuals = true;
  }

  return {
    ...teamType,
    tickets: [
      ...teamType.tickets.filter(({ ticket_id }) => ticket_id !== ticketId),
      ...(hasTeamActions(nextTicket) ? [nextTicket] : []),
    ],
  };
};

const setTicketTeamsAllowed = (
  teamType: TeamTypeFormData,
  ticketId: string,
  allowed: boolean,
  defaultMode: TeamPermissionMode = 'create_join',
  defaultAllowIndividuals = true,
): TeamTypeFormData => {
  const nextTeamType = setTicketTeamActions(teamType, ticketId, allowed ? defaultMode : 'none');

  return setTicketPermission(
    nextTeamType,
    ticketId,
    'allow_individuals',
    allowed ? defaultAllowIndividuals : true,
  );
};

const setTicketTeamsRequired = (
  teamType: TeamTypeFormData,
  ticketId: string,
  required: boolean,
): TeamTypeFormData => {
  const existing = getTicketPermissions(teamType, ticketId);

  if (!hasTeamActions(existing)) {
    return teamType;
  }

  return setTicketPermission(teamType, ticketId, 'allow_individuals', !required);
};

const getTicketOwners = (teamTypes: TeamTypeFormData[]) => teamTypes.reduce<Record<string, number>>(
  (owners, teamType, index) => {
    teamType.tickets
      .filter(hasTeamActions)
      .forEach((ticket) => {
        if (owners[ticket.ticket_id] === undefined) {
          owners[ticket.ticket_id] = index;
        }
      });

    return owners;
  },
  {},
);

const getTicketCategoryGroups = (event: Event): TicketCategoryGroup[] => {
  const ticketsByCategory: Record<string, Ticket[]> = {};

  event.tickets.forEach((ticket) => {
    const ticketCategoryId = ticket.ticket_category?.id;

    if (!ticketCategoryId) {
      return;
    }

    if (!ticketsByCategory[ticketCategoryId]) {
      ticketsByCategory[ticketCategoryId] = [];
    }

    ticketsByCategory[ticketCategoryId].push(ticket);
  });

  return event.ticket_categories
    .map((category) => ({
      id: category.id,
      title: category.title || category.id,
      tickets: ticketsByCategory[category.id] || [],
    }))
    .filter((category) => category.tickets.length > 0);
};

export interface EditEventTeamSettingsFormProps {
  event: Event;
}

const EditEventTeamSettingsForm = ({ event }: EditEventTeamSettingsFormProps) => {
  const { t } = useTranslation();
  const ticketCategoryGroups = getTicketCategoryGroups(event);
  const nextTeamTypeKey = useRef(0);

  const form = useForm<EditEventTeamSettingsFormValues>({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      teams: {
        team_types: getSaveTeamTypesInput(form.values.team_types),
        allow_individual_tickets: getPermissionTicketIds(form.values.team_types, 'allow_individuals'),
        allow_create_team_tickets: getPermissionTicketIds(form.values.team_types, 'allow_create_team'),
        allow_join_team_tickets: getPermissionTicketIds(form.values.team_types, 'allow_join_team'),
      },
    },
    onSuccess: (updatedEvent) => {
      form.reset(getValues(updatedEvent));
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventTeamSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'teams'),
  });

  const addTeamType = () => {
    form.set((values) => ({
      ...values,
      team_types: [
        ...values.team_types,
        {
          form_key: `new-${nextTeamTypeKey.current++}`,
          title: '',
          title_translations: [],
          join_policy: JoinPolicy.public,
          member_visibility: MemberVisibility.hidden,
          max_team_size: null,
          deletable: true,
          tickets: values.team_types.length === 0 && event.tickets.length === 1 ? event.tickets.map((ticket) => {
            const { allowCreateTeam, allowJoinTeam } = getTeamActionFlagsFromMode(getDefaultPermissionMode(ticket));

            return {
              ticket_id: ticket.id,
              allow_individuals: getDefaultAllowIndividuals(ticket),
              allow_create_team: allowCreateTeam,
              allow_join_team: allowJoinTeam,
            };
          }) : [],
        },
      ],
    }));
  };

  const removeTeamType = (index: number) => {
    form.delete('team_types', index);
  };

  const moveTeamTypeUp = (index: number) => {
    form.moveUp('team_types', index);
  };

  const moveTeamTypeDown = (index: number) => {
    form.moveDown('team_types', index);
  };

  const ticketOwners = getTicketOwners(form.values.team_types);

  const onTicketTeamsAllowedChange = (
    teamTypeIndex: number,
    ticketId: string,
    checked: boolean,
  ) => {
    const ticket = event.tickets.find((eventTicket) => eventTicket.id === ticketId);
    const defaultMode = ticket ? getDefaultPermissionMode(ticket) : 'create_join';
    const defaultAllowIndividuals = ticket ? getDefaultAllowIndividuals(ticket) : true;

    form.set((values) => ({
      ...values,
      team_types: values.team_types.map((teamType, index) => (
        index === teamTypeIndex
          ? setTicketTeamsAllowed(teamType, ticketId, checked, defaultMode, defaultAllowIndividuals)
          : teamType
      )),
    }));
  };

  const onTicketTeamsRequiredChange = (
    teamTypeIndex: number,
    ticketId: string,
    checked: boolean,
  ) => {
    form.set((values) => ({
      ...values,
      team_types: values.team_types.map((teamType, index) => (
        index === teamTypeIndex ? setTicketTeamsRequired(teamType, ticketId, checked) : teamType
      )),
    }));
  };

  const onTicketPermissionModeChange = (
    teamTypeIndex: number,
    ticketId: string,
    mode: TeamPermissionMode,
  ) => {
    form.set((values) => ({
      ...values,
      team_types: values.team_types.map((teamType, index) => (
        index === teamTypeIndex ? setTicketTeamActions(teamType, ticketId, mode) : teamType
      )),
    }));
  };

  return (
    <UI.Form onSubmit={() => editEvent()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} sc={{ mb: 4 }} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('teams')}</UI.Legend>
          <UI.InputDescription>
            {t('edit_event_teams_description')}
          </UI.InputDescription>
        </UI.Div>

        {form.values.team_types.map((value, index) => (
          <TeamTypeForm
            form={form}
            index={index}
            errors={errors}
            event={event}
            ticketCategoryGroups={ticketCategoryGroups}
            ticketOwners={ticketOwners}
            canMoveUp={index > 0}
            canMoveDown={index < form.values.team_types.length - 1}
            onMoveUp={() => moveTeamTypeUp(index)}
            onMoveDown={() => moveTeamTypeDown(index)}
            onDelete={() => removeTeamType(index)}
            onTicketTeamsAllowedChange={onTicketTeamsAllowedChange}
            onTicketTeamsRequiredChange={onTicketTeamsRequiredChange}
            onTicketPermissionModeChange={onTicketPermissionModeChange}
            key={value.form_key}
          />
        ))}

        <UI.Div>
          <UI.Button onClick={addTeamType}>
            {form.values.team_types.length === 0 ? t('enable_teams') : t('add_team_type')}
          </UI.Button>
        </UI.Div>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface TeamTypeFormProps {
  form: FormManager<EditEventTeamSettingsFormValues>;
  index: number;
  errors: ErrorBag;
  event: Event;
  ticketCategoryGroups: TicketCategoryGroup[];
  ticketOwners: Record<string, number>;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onTicketTeamsAllowedChange: (
    teamTypeIndex: number,
    ticketId: string,
    checked: boolean,
  ) => void;
  onTicketTeamsRequiredChange: (
    teamTypeIndex: number,
    ticketId: string,
    checked: boolean,
  ) => void;
  onTicketPermissionModeChange: (
    teamTypeIndex: number,
    ticketId: string,
    mode: TeamPermissionMode,
  ) => void;
}

const TeamTypeForm = ({
  form,
  index,
  errors,
  event,
  ticketCategoryGroups,
  ticketOwners,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onDelete,
  onTicketTeamsAllowedChange,
  onTicketTeamsRequiredChange,
  onTicketPermissionModeChange,
}: TeamTypeFormProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const integerInput = useIntegerInput();

  const value = form.values.team_types[index];

  const teamType = event.team_types.find((teamType) => teamType.id === value.id);
  const deletable = teamType?.deletable ?? true;
  const teamTypeLabel = teamType?.title || t('team_type_number', { number: formatNumber(index + 1) });

  const key = (k?: string) => `team_types.${index}${k ? `.${k}` : ''}`;

  const isOwnedByOtherTeamType = (ticketId: string) => {
    const owner = ticketOwners[ticketId];

    return typeof owner !== 'undefined' && owner !== index;
  };

  const [showingTicketPermissions, setShowingTicketPermissions] = useState(false);

  return (
    <UI.Card sc={{ outline: 'gray.200', hasPanels: true, borderRadius: 'lg' }}>
      <UI.FlexContainer
        sc={{ justifyContent: 'space-between', alignItems: 'center', background: 'gray.50', py: 3 }}
      >
        <UI.FlexContainer
          sc={{ justifyContent: 'space-between', alignItems: 'center', gap: 0.25 }}
        >
          <UI.Legend sc={{ fontWeight: 500 }}>
            <UI.Icon>
              <Settings />
            </UI.Icon>
            {' '}
            {t('team_type_number', { number: formatNumber(index + 1) })}
            {' '}
          </UI.Legend>
          {teamType?.title && (
            <UI.Label sc={{ small: true }}>
              {teamType.title}
            </UI.Label>
          )}
        </UI.FlexContainer>
        <UI.FlexContainer sc={{ alignItems: 'center', my: -0.75 }}>
          <UI.Div sc={{ small: true, mr: 2 }}>
            {t('teams_count', { value: teamType?.teams_count || 0, count: teamType?.teams_count || 0 })}
          </UI.Div>
          <UI.Button
            onClick={onMoveUp}
            disabled={!canMoveUp}
            sc={{ blank: true, color: 'gray.500', size: 'sm' }}
            aria-label={t('move_up_option', { option: teamTypeLabel })}
          >
            <UI.Icon>
              <ArrowUp />
            </UI.Icon>
          </UI.Button>
          <UI.Button
            onClick={onMoveDown}
            disabled={!canMoveDown}
            sc={{ blank: true, color: 'gray.500', size: 'sm' }}
            aria-label={t('move_down_option', { option: teamTypeLabel })}
          >
            <UI.Icon>
              <ArrowDown />
            </UI.Icon>
          </UI.Button>
          <UI.Button
            onClick={onDelete}
            sc={{ blank: true, color: 'gray.500', size: 'sm' }}
            aria-label={t('delete_option', { option: teamTypeLabel })}
            disabled={!deletable}
          >
            <UI.Icon>
              <Trash2 />
            </UI.Icon>
          </UI.Button>
        </UI.FlexContainer>
      </UI.FlexContainer>
      <UI.FormGrid>
        <UI.Div>
          <UI.Legend>
            {t('basic_info')}
          </UI.Legend>
          <UI.InputDescription>
            {t('team_type_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup>
            <UI.InputLabel>
              {t('join_policy')}
            </UI.InputLabel>

            <UI.RadioPill
              sc={{ fullWidth: true }}
              // Remount after rerendering, because radio group name changes on reordering team types,
              // causing radio buttons to visually appear unchecked.
              key={`join_policy-${value.form_key}-${index}`}
            >
              <UI.Radio
                {...form.radio(key('join_policy'), JoinPolicy.public)}
                value={JoinPolicy.public}
                sc={{ box: true, fontWeight: 500 }}
              >
                <UI.Div style={{ display: 'inline-flex' }}>
                  <UI.Span sc={{ mr: 0.75 }}>
                    {t('join_policies.public')}
                  </UI.Span>
                  <UI.TooltipContainer sc={{ margin: -0.75, padding: 0.75 }}>
                    <UI.Icon sc={{ color: 'gray.500' }}>
                      <HelpCircle />
                    </UI.Icon>
                    <UI.Tooltip sc={{ alignH: 'center' }}>
                      {t('join_policies.public_description')}
                    </UI.Tooltip>
                  </UI.TooltipContainer>
                </UI.Div>
              </UI.Radio>
              <UI.Radio
                {...form.radio(key('join_policy'), JoinPolicy.private)}
                value={JoinPolicy.private}
                sc={{ box: true, fontWeight: 500 }}

              >
                <UI.Div style={{ display: 'inline-flex' }}>
                  <UI.Span sc={{ mr: 0.75 }}>
                    {t('join_policies.private')}
                  </UI.Span>
                  <UI.TooltipContainer sc={{ margin: -0.75, padding: 0.75 }}>
                    <UI.Icon sc={{ color: 'gray.500' }}>
                      <HelpCircle />
                    </UI.Icon>
                    <UI.Tooltip sc={{ alignH: 'center' }}>
                      {t('join_policies.private_description')}
                    </UI.Tooltip>
                  </UI.TooltipContainer>
                </UI.Div>
              </UI.Radio>
            </UI.RadioPill>
          </UI.InputGroup>

          <UI.InputGroup>
            <UI.InputLabel>
              {t('member_visibility')}
            </UI.InputLabel>
            <UI.RadioPill
              sc={{ fullWidth: true }}
              // Remount after rerendering, because radio group name changes on reordering team types,
              // causing radio buttons to visually appear unchecked.
              key={`member_visibility-${value.form_key}-${index}`}
            >
              <UI.Radio
                {...form.radio(key('member_visibility'), MemberVisibility.visible)}
                sc={{ box: true, fontWeight: 500 }}
              >
                <UI.Div style={{ display: 'inline-flex' }}>
                  <UI.Span sc={{ mr: 0.75 }}>
                    {t('member_visibilities.visible')}
                  </UI.Span>
                  <UI.TooltipContainer sc={{ margin: -0.75, padding: 0.75 }}>
                    <UI.Icon sc={{ color: 'gray.500' }}>
                      <HelpCircle />
                    </UI.Icon>
                    <UI.Tooltip sc={{ alignH: 'center' }}>
                      {t('member_visibilities.visible_description')}
                    </UI.Tooltip>
                  </UI.TooltipContainer>
                </UI.Div>
              </UI.Radio>
              <UI.Radio
                {...form.radio(key('member_visibility'), MemberVisibility.hidden)}
                sc={{ box: true, fontWeight: 500 }}
              >
                <UI.Div style={{ display: 'inline-flex' }}>
                  <UI.Span sc={{ mr: 0.75 }}>
                    {t('member_visibilities.hidden')}
                  </UI.Span>
                  <UI.TooltipContainer sc={{ margin: -0.75, padding: 0.75 }}>
                    <UI.Icon sc={{ color: 'gray.500' }}>
                      <HelpCircle />
                    </UI.Icon>
                    <UI.Tooltip sc={{ alignH: 'center' }}>
                      {t('member_visibilities.hidden_description')}
                    </UI.Tooltip>
                  </UI.TooltipContainer>
                </UI.Div>
              </UI.Radio>
            </UI.RadioPill>
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup>
            <UI.InputLabel htmlFor={`MaxTeamSize_${index}`}>
              {t('max_team_size')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('max_team_size_description')}
            </UI.InputDescription>
            <UI.DebouncedInput
              {...form.text(key('max_team_size'), { inputHandler: integerInput() })}
              format={integerInput().format}
              inputMode="numeric"
              match={/[\d]/g}
              id={`MaxTeamSize_${index}`}
            />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors[key('title')], touched: form.getTouched(key('title')) }}>
            <UI.InputLabel htmlFor={`TeamTypeTitle_${index}`}>
              {t('team_type_title')}
              {' '}
              {index > 0 && (
                <UI.RequiredMark />
              )}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('visible_to_participants')}
            </UI.InputDescription>
            <TranslatableInput
              form={form}
              errors={errors}
              name={key('title')}
              defaultLocale={event.locale}
              title={t('team_type_title')}
              maxLength={25}
              placeholder={t('team_type_title_placeholder')}
              id={`TeamTypeTitle_${index}`}
            />
            <UI.ErrorMessages attribute={t('team_type_title')} errors={errors[key('title')]} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.Div>
          <UI.A role="button" onClick={() => setShowingTicketPermissions((showing) => !showing)}>
            <UI.Legend>
              {t('tickets_and_permissions')}
              {' '}
              <UI.Icon>
                {showingTicketPermissions ? <ChevronUp /> : <ChevronDown />}
              </UI.Icon>
            </UI.Legend>
          </UI.A>
          <UI.AnimateHeight isVisible={showingTicketPermissions}>
            <UI.FormGrid sc={{ mt: 1 }}>
              <UI.InputDescription>
                {t('team_type_tickets_description')}
              </UI.InputDescription>

              {ticketCategoryGroups.map((ticketCategory) => (
                <UI.FormGrid sc={{ gutter: 0.25 }} key={ticketCategory.id}>
                  <UI.FormGrid sc={{ columns: '2fr 1fr 1fr', gutter: 0.25 }}>
                    <UI.Div>
                      <UI.InputLabel>
                        {ticketCategoryGroups.length > 1 ? ticketCategory.title : t('ticket')}
                      </UI.InputLabel>
                    </UI.Div>
                    <UI.Div>
                      <UI.InputLabel>
                        {t('required')}
                      </UI.InputLabel>
                    </UI.Div>
                    <UI.Div>
                      <UI.InputLabel>
                        {t('permissions')}
                      </UI.InputLabel>
                    </UI.Div>
                  </UI.FormGrid>

                  <UI.HR sc={{ dotted: true }} />

                  <UI.FormGrid sc={{ gutter: 0.25 }}>
                    {ticketCategory.tickets.map((ticket) => {
                      const disabled = isOwnedByOtherTeamType(ticket.id);
                      const permissions = getTicketPermissions(value, ticket.id);
                      const teamsAllowed = hasTeamActions(permissions);
                      const teamsRequired = requiresTeams(permissions);
                      const permissionMode = getTeamPermissionMode(permissions);

                      return (
                        <UI.FormGrid
                          sc={{ columns: '2fr 1fr 1fr', alignVertical: 'center', gutter: 0.25 }}
                          key={ticket.id}
                        >
                          <UI.Div>
                            <UI.Checkbox
                              checked={teamsAllowed}
                              onChange={() => onTicketTeamsAllowedChange(index, ticket.id, !teamsAllowed)}
                              disabled={disabled}
                            >
                              {ticket.title}
                            </UI.Checkbox>
                          </UI.Div>
                          <UI.Div>
                            <UI.Checkbox
                              checked={teamsRequired}
                              onChange={() => onTicketTeamsRequiredChange(index, ticket.id, !teamsRequired)}
                              disabled={disabled || !teamsAllowed}
                              aria-label={`${t('team_required')} · ${ticket.title}`}
                            >
                              {t('team_required')}
                            </UI.Checkbox>
                          </UI.Div>
                          <UI.Div>
                            <UI.Select
                              value={permissionMode}
                              disabled={disabled || !teamsAllowed}
                              onChange={(e) => onTicketPermissionModeChange(
                                index,
                                ticket.id,
                                e.target.value as TeamPermissionMode,
                              )}
                              aria-label={`${t('permissions')} · ${ticket.title}`}
                              sc={{ size: 'xs' }}
                            >
                              {!teamsAllowed && (
                                <option value="none">
                                  {t('none')}
                                </option>
                              )}
                              {teamPermissionModeOptions.map((option) => (
                                <option value={option.value} key={option.value}>
                                  {t(option.label)}
                                </option>
                              ))}
                            </UI.Select>
                          </UI.Div>
                        </UI.FormGrid>
                      );
                    })}
                  </UI.FormGrid>
                </UI.FormGrid>
              ))}
            </UI.FormGrid>
          </UI.AnimateHeight>
        </UI.Div>
      </UI.FormGrid>
    </UI.Card>
  );
};

export default EditEventTeamSettingsForm;
